export type SubscriptionTier = 'free' | 'premium' | 'enterprise';

export interface Feature {
  id: string;
  name: string;
  requiredTier: SubscriptionTier;
  description: string;
  upgradeMessage?: string;
}

/**
 * Central feature registry for premium feature gates
 * Add new gated features here to enable feature gating across the app
 */
export const FEATURES = {
  BRANDING: {
    id: 'branding',
    name: 'Custom Branding',
    requiredTier: 'premium' as SubscriptionTier,
    description: 'Customize colors, logos, QR codes, and web embed customization.',
    upgradeMessage: 'Custom branding is available on premium plans. Upgrade to unlock colors, logos, QR codes, and web embed customization.',
  },
  ADVANCED_VOICE: {
    id: 'advanced_voice',
    name: 'Advanced Voice Settings',
    requiredTier: 'premium' as SubscriptionTier,
    description: 'Fine-tune voice parameters, stability, and similarity boost.',
    upgradeMessage: 'Advanced voice settings are available on premium plans. Upgrade to unlock fine-grained voice controls.',
  },
  CUSTOM_PRONUNCIATION: {
    id: 'custom_pronunciation',
    name: 'Custom Pronunciations',
    requiredTier: 'premium' as SubscriptionTier,
    description: 'Add custom pronunciation rules for names, brands, and technical terms.',
    upgradeMessage: 'Custom pronunciations are available on premium plans. Upgrade to teach your genie how to pronounce specific words.',
  },
  ADVANCED_KNOWLEDGE: {
    id: 'advanced_knowledge',
    name: 'Advanced Knowledge Base',
    requiredTier: 'premium' as SubscriptionTier,
    description: 'Upload unlimited documents and enable advanced knowledge features.',
    upgradeMessage: 'Advanced knowledge base features are available on premium plans. Upgrade to unlock unlimited document uploads.',
  },
} as const;

export type FeatureId = keyof typeof FEATURES;

/**
 * Get feature configuration by ID
 */
export const getFeature = (featureId: FeatureId): Feature => {
  return FEATURES[featureId];
};

/**
 * Check if a tier meets the requirement for a feature
 */
export const tierMeetsRequirement = (
  userTier: SubscriptionTier,
  requiredTier: SubscriptionTier
): boolean => {
  const tierHierarchy: Record<SubscriptionTier, number> = {
    free: 0,
    premium: 1,
    enterprise: 2,
  };

  return tierHierarchy[userTier] >= tierHierarchy[requiredTier];
};
